import styled, { DefaultTheme } from 'styled-components';

import { FlexRowCenterVertical } from '@main/core-ui';
import { sp } from '@main/theme';
import { hexToRgb } from '@main/theme-types';

interface CheckboxInputContainerProps {
  /** flag for whether checkbox input is disabled */
  disabled: boolean;
  /** current checked state of checkbox input */
  checkboxInputValue: 'checked' | 'unchecked';
  /** styled-components theme set up by a theme provider in each app */
  theme: DefaultTheme;
}

const hexToHalfOpacity = (hexColor: string): string => {
  const { r, g, b } = hexToRgb(hexColor);
  return `rgba(${r}, ${g}, ${b}, 0.5)`;
};

/**
 * ui state of the checkbox input div
 */
type CheckboxInputUIState = 'hover' | 'active' | 'disabled';

/**
 * Utility function to get a mapping of ui states to colors from a given theme
 */
type GetColorsMap = (theme: DefaultTheme) => {
  [key in CheckboxInputUIState]: { [key in 'checked' | 'unchecked']: string };
};

const getBorderColorsMap: GetColorsMap = ({ colors }: DefaultTheme) => ({
  hover: {
    unchecked: colors.primary,
    checked: colors.primary,
  },
  active: {
    unchecked: colors.primary,
    checked: colors.primary,
  },
  disabled: {
    unchecked: colors.gray3,
    checked: hexToHalfOpacity(colors.primary),
  },
});

const getBackgroundColorsMap: GetColorsMap = ({ colors }: DefaultTheme) => ({
  hover: {
    unchecked: colors.white,
    checked: colors.darkPrimary,
  },
  active: {
    unchecked: colors.white,
    checked: colors.primary,
  },
  disabled: {
    unchecked: colors.white,
    checked: hexToHalfOpacity(colors.primary),
  },
});
const getColor =
  (colorsMapGetter: GetColorsMap) =>
  (checkboxInputUIState: CheckboxInputUIState) =>
  ({ disabled, checkboxInputValue, theme }: CheckboxInputContainerProps) =>
    colorsMapGetter(theme)[disabled ? 'disabled' : checkboxInputUIState][
      checkboxInputValue
    ];
const getBorderColor = getColor(getBorderColorsMap);
const getBackgroundColor = getColor(getBackgroundColorsMap);

export const HiddenCheckbox = styled.input`
  opacity: 0;
  width: 0;
  position: absolute;

  // color for label when checkbox input is checked
  :checked + label {
    color: ${({ theme }) => getBorderColorsMap(theme).active.checked};
  }

  // color for checkbox input border when checked
  :checked + label > div {
    background-size: 100%;
    border-color: ${({ theme }) => getBorderColorsMap(theme).active.checked};
    background: ${({ theme }) => getBackgroundColorsMap(theme).active.checked};
  }

  // color for label when checkbox input is disabled and checked
  :checked :disabled + label {
    color: ${({ theme }) => getBackgroundColorsMap(theme).disabled.checked};
  }

  // color for checkbox input box when disabled and checked
  :checked :disabled + label > div {
    background-size: 100%;
    border-color: ${({ theme }) => getBorderColorsMap(theme).disabled.checked};
    background: ${({ theme }) =>
      getBackgroundColorsMap(theme).disabled.checked};
  }

  // properties for label when checkbox input is disabled
  :disabled ~ label {
    color: ${({ theme }) => theme.colors.textLightOnBg};
    cursor: not-allowed;
  }

  // color for checkbox input box when disabled
  :disabled + label > div {
    border-color: ${({ theme }) =>
      getBorderColorsMap(theme).disabled.unchecked};
    cursor: not-allowed;
  }
`;

export const CheckboxInputContainer = styled(FlexRowCenterVertical)`
  position: relative;
  border-radius: ${sp(1)};
  border-width: 1px;
  border-style: solid;
  padding: 6px;
  border-color: ${({ checkboxInputValue, theme }) =>
    checkboxInputValue === 'checked'
      ? theme.colors.primary
      : theme.colors.gray3};

  :hover > input[type='checkbox'] :not(:disabled) + label {
    color: ${getBorderColor('hover')};
  }

  :hover > input[type='checkbox'] :not(:disabled) + label > div {
    border-color: ${getBorderColor('hover')};
    background: ${getBackgroundColor('hover')};
  }

  :active > input[type='checkbox'] :not(:disabled) + label {
    color: ${getBorderColor('active')};
  }

  :active > input[type='checkbox'] :not(:disabled) + label > div {
    transform: scale(90%);
    transition: transform 0ms;
    border-color: ${getBorderColor('active')};
    background: ${getBackgroundColor('active')};
  }

  :disabled {
    cursor: not-allowed;
  }
`;

export const CheckmarkContainer = styled.div`
  position: relative;
  height: 16px;
  width: 16px;
  min-height: 16px;
  min-width: 16px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  cursor: pointer;
  border-width: 1px;
  border-style: solid;
  background-size: 0;
  transition: all 200ms ease-out;
  border-color: ${({ theme }) => theme.colors.gray3};
`;

export const LabelWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${sp(1)};
`;
